/**
 * chooserQuestions — pure data + logic for the #1922 "Help me choose" guest
 * chooser (Navigator pattern, 3 questions, approved 2026-08-18).
 *
 * #1768 one-recommender constraint: everything in this file is
 * PRESENTATION-LAYER parameterisation of the EXISTING shelf/shortlist —
 * never a second recommender.
 *   - Q1 picks WHICH already-built context shortlist gets loaded, with no
 *     new scoring — it's the same `/discovery/context/:slug/shortlist` the
 *     plain contexts list already calls.
 *   - Q2 only REORDERS cards the server already returned, via a small,
 *     transparent, deterministic keyword match against name+summary —
 *     never an LLM, never per-user state, never a re-score.
 *   - Q3 only sets which `CommitmentSheet` modality default carries
 *     through (same field the sheet's own toggle already exposes).
 *
 * No React here — importable by both the chooser page and its tests
 * without dragging in JSX, matching the `contextPresentation.js` precedent
 * in this same directory.
 */

/** Q1 "Who's the conversation with?" — maps directly to the existing
 * context taxonomy (`backend/src/constants/scenarioContexts.js`). Only 5 of
 * the 7 taxonomy slugs are guest-visible front-door contexts (#2098 call
 * 6); this list is exactly those 5, so the chooser can never route toward
 * an excluded context. */
export const CHOOSER_Q1_OPTIONS = [
  { label: 'A customer or client', context: 'serving_people' },
  { label: 'Someone I manage', context: 'leading_people' },
  { label: 'My manager or someone senior', context: 'speaking_up' },
  { label: 'A colleague', context: 'at_work' },
  { label: 'A recruiter or interviewer', context: 'my_own_career' },
];

/** Q2 "What are you trying to do?" — reorders WITHIN the Q1 context. */
export const CHOOSER_Q2_OPTIONS = [
  { label: 'Deliver difficult news', intent: 'deliver_news' },
  { label: 'Push back or disagree', intent: 'push_back' },
  { label: 'Ask for something', intent: 'ask_for_something' },
  { label: 'Handle emotion or conflict', intent: 'handle_emotion' },
];

/** Q3 "Talk it out loud, or type it?" — sets the same `preferredMode` field
 * J4's commitment sheet (and ChatPage's briefing skip, #2097) already read.
 * No duration question anywhere in this file (gate constraint: duration
 * never as entry/exit deterrent). */
export const CHOOSER_Q3_OPTIONS = [
  { label: 'Talk it out loud', mode: 'voice' },
  { label: 'Type it', mode: 'text' },
];

/**
 * Deterministic, transparent keyword sets per Q2 intent, matched against a
 * card's `name` + `summary` (lowercased substring match). The discovery
 * card shape (`toDiscoveryCard`, backend/src/routes/demo.js) has no tags
 * field, so text is the only signal available at this layer — each list is
 * short and legible on purpose: this constant IS the whole "model", which
 * is what keeps it a presentation nudge rather than a second recommender.
 */
export const CHOOSER_INTENT_KEYWORDS = Object.freeze({
  deliver_news: [
    'layoff', 'lay off', 'fired', 'firing', 'terminat', 'bad news', 'cut',
    'cancel', 'reject', 'declin', 'delay', 'underperform', 'downsiz', 'budget cut',
  ],
  push_back: [
    'disagree', 'push back', 'pushback', 'boundary', 'boundaries', 'object',
    'challenge', 'unfair', 'refuse', 'say no', 'confront', 'pressure',
  ],
  ask_for_something: [
    'ask for', 'request', 'raise', 'promotion', 'negotiat', 'favor', 'favour',
    'resource', 'extension', 'help with', 'support for',
  ],
  handle_emotion: [
    'upset', 'angry', 'anger', 'frustrat', 'conflict', 'emotion', 'crying',
    'tension', 'defensive', 'stress', 'anxious', 'difficult conversation',
  ],
});

function matchScore(card, intent) {
  const keywords = CHOOSER_INTENT_KEYWORDS[intent];
  if (!keywords || !keywords.length) return 0;
  const haystack = `${card?.name || ''} ${card?.summary || ''}`.toLowerCase();
  return keywords.reduce((count, kw) => (haystack.includes(kw) ? count + 1 : count), 0);
}

/**
 * Reorder `cards` (already fetched, already ranked by the server) so
 * scenarios matching the chosen Q2 intent's keywords float to the top — a
 * STABLE partition: matches first (in their original relative order), then
 * non-matches (in their original relative order). Never a full re-score.
 *
 * Stable fallback (returns the input order, as a new array): no intent
 * given (skipped Q2), an unrecognised intent key, or an intent with zero
 * matches anywhere in the list — the existing shelf ranking is never made
 * worse by this pass.
 *
 * @param {Array<object>} cards
 * @param {string|null|undefined} intent - one of CHOOSER_Q2_OPTIONS' `intent`
 *   values, or null/undefined when Q2 was skipped
 * @returns {Array<object>} a NEW array — never mutates `cards`
 */
export function reorderByIntent(cards, intent) {
  const list = Array.isArray(cards) ? cards : [];
  if (!intent || !CHOOSER_INTENT_KEYWORDS[intent]) return [...list];

  const scored = list.map((card, index) => ({ card, index, score: matchScore(card, intent) }));
  const anyMatch = scored.some((s) => s.score > 0);
  if (!anyMatch) return [...list];

  return scored
    .slice()
    .sort((a, b) => (b.score !== a.score ? b.score - a.score : a.index - b.index))
    .map((s) => s.card);
}
